import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import '../Dashboard/Dashboard.css';
import './EventManagement.css';
import useQueryParamState from '../../hooks/useQueryParamState';
import AdminHeader from '../Layout/AdminHeader';
import AdminSidebar from '../Layout/AdminSidebar';
import PageInstructionBanner from '../common/PageInstructionBanner';
import filterIcon from '../../assets/filter.png';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject as GridInject, Sort, Filter, Toolbar as GridToolbar, Selection, Resize, Reorder, ColumnChooser, ColumnMenu, Freeze } from '@syncfusion/ej2-react-grids';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { Input, DateTimePicker, Checkbox, Select, MultiSelect } from '../UI/FormComponents';
import { 
    RichTextEditorComponent as RTE, 
    Toolbar as RTEToolbar, 
    Link as RteLink, 
    Image as RteImage, 
    HtmlEditor, 
    QuickToolbar,
    Table,
    Video,
    Audio,
    EmojiPicker,
    PasteCleanup,
    Count,
    Resize as RTEResize,
    FormatPainter,
    Inject as RTEInject 
} from '@syncfusion/ej2-react-richtexteditor';
import { RTE_QUICK_TOOLBAR_SETTINGS, getInsertImageSettings, getInsertVideoSettings, getInsertAudioSettings, handleRteKeyDown } from '../../utils/rteConfig';
import { uploadImageToS3, uploadVideoToS3, uploadAudioToS3 } from '../../services/uploads';
import {
    LOGO_ACCEPT,
    getLogoUploadErrorMessage,
    validateLogoFile,
} from '../../utils/logoUpload';
import VideoUploadProgress from '../UI/VideoUploadProgress';
import { closeRteMediaDialog, isVideoFile, isAudioFile, generateVideoHTML, generateAudioHTML } from '../../utils/rteDialogHelper';
import { listEvents, createEvent, updateEvent, deleteEvent, bulkDeleteEvents } from '../../services/events';
import { getSendyLists } from '../../services/sendy';
import { termsConditionsAPI } from '../../services/termsConditions';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SYNC_GRID_FILTER_SETTINGS, SYNC_GRID_CHECKBOX_COLUMN_PROPS } from '../../utils/syncfusionGridHelpers';

export default function EventManagement() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        if (!loading) {
            if (!user) {
                navigate('/login', { replace: true });
            } else if (!['Admin', 'GlobalSupport'].includes(user.role)) {
                navigate('/dashboard', { replace: true });
            }
        }
    }, [user, loading, navigate]);

    // Set CSS variable for filter icon and make it trigger column menu
    useEffect(() => {
        if (!gridRef.current) return;
        
        const filterIconUrl = `url(${filterIcon})`;
        
        // Set CSS variable on document root
        document.documentElement.style.setProperty('--filter-icon-url', filterIconUrl);
        
        const grid = gridRef.current;
        
        // Override filter icon click to open column menu instead
        const handleFilterIconClick = (e) => {
            const filterIcon = e.target.closest('.e-filtericon');
            if (!filterIcon) return;
            
            e.stopPropagation();
            e.preventDefault();
            
            const headerCell = filterIcon.closest('.e-headercell');
            if (!headerCell || !grid.columnMenuModule) return;
            
            // Get column field from header cell
            const columnIndex = Array.from(headerCell.parentElement.children).indexOf(headerCell);
            const column = grid.columns[columnIndex];
            
            if (column) {
                // Open column menu
                grid.columnMenuModule.openColumnMenu(headerCell, column, e);
            }
        };
        
        // The filter-icon image is styled entirely via CSS (--filter-icon-url), so no
        // per-mutation DOM observer is needed. We only intercept clicks on the grid
        // element to open the column menu. Runs once on mount.
        const gridElement = grid.element;
        if (gridElement) {
            gridElement.addEventListener('click', handleFilterIconClick, true);
        }
        
        return () => {
            document.documentElement.style.removeProperty('--filter-icon-url');
            if (gridElement) {
                gridElement.removeEventListener('click', handleFilterIconClick, true);
            }
        };
    }, []);

    const [mode, setMode] = useState('list'); // 'list' | 'create' | 'edit'
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    
    // Upload progress state
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadingFile, setUploadingFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [rowPendingDelete, setRowPendingDelete] = useState(null);
    const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const toastRef = useRef(null);
    const deleteDialogRef = useRef(null);
    const gridRef = useRef(null);
    const loadingListRef = useRef(false);

    // RTE image upload helpers (Event Information editor)
    const rteInfoRef = useRef(null);

    const [form, setForm] = useState({
        sendyId: '',
        name: '',
        link: '',
        logoUrl: '',
        logoAltText: '',
        maxBooths: 0,
        maxRecruitersPerEvent: 0,
        startTime: '',
        endTime: '',
        information: '',
        termsId: '',
        termsIds: [],
        status: 'draft',
        isDemo: false,
    });

    const [sendyLists, setSendyLists] = useState([]);
    const [sendyListsLoading, setSendyListsLoading] = useState(false);
    const [manualSendyId, setManualSendyId] = useState('');

    const connectedSendyListName = useMemo(() => {
        if (!form.sendyId) return '';
        const match = sendyLists.find((list) => list.id === form.sendyId);
        return match?.name || '';
    }, [form.sendyId, sendyLists]);

    useEffect(() => {
        if (mode !== 'create' && mode !== 'edit') return undefined;

        let cancelled = false;

        const loadSendyLists = async () => {
            setSendyListsLoading(true);
            try {
                const data = await getSendyLists();
                if (!cancelled) {
                    setSendyLists(data.lists || []);
                }
            } catch (error) {
                if (!cancelled) {
                    setSendyLists([]);
                    console.warn('Failed to load Sendy lists', error);
                }
            } finally {
                if (!cancelled) {
                    setSendyListsLoading(false);
                }
            }
        };

        loadSendyLists();

        return () => {
            cancelled = true;
        };
    }, [mode]);

    useEffect(() => {
        if (mode === 'create' || mode === 'edit') {
            setManualSendyId(form.sendyId || '');
        }
    }, [form.sendyId, mode]);

    // Toast notification helper - defined early so it can be used in RTE handlers
    const showToast = (message, type = 'Success') => {
        if (toastRef.current) {
            toastRef.current.show({
                title: type,
                content: message,
                cssClass: `e-toast-${type.toLowerCase()}`,
                showProgressBar: true,
                timeOut: 3000
            });
        }
    };

    // Memoize toolbar settings so Syncfusion RTE doesn't reinitialize on every render
    const rteToolbarSettings = useMemo(() => ({
        type: 'Expand',
        enableFloating: true,
        items: [
            'Undo', 'Redo', '|',
            'Bold', 'Italic', 'Underline', 'StrikeThrough', 'InlineCode', '|',
            'FontName', 'FontSize', 'FontColor', 'BackgroundColor', '|',
            'LowerCase', 'UpperCase', '|',
            'SuperScript', 'SubScript', '|',
            'Formats', 'Alignments', '|',
            'OrderedList', 'UnorderedList', '|',
            'Outdent', 'Indent', '|',
            'CreateLink', 'Image', 'Video', 'Audio', '|',
            'CreateTable', '|',
            'EmojiPicker', '|',
            'ClearFormat', '|',
            'Print', 'FullScreen', '|',
            'SourceCode'
        ]
    }), []);

    /** Add auth header so Syncfusion's built-in uploader can reach POST /api/uploads/rte-image */
    const handleImageUploading = useCallback((args) => {
        const token = localStorage.getItem('token');
        if (args.currentRequest && token) {
            args.currentRequest.setRequestHeader('Authorization', `Bearer ${token}`);
        }
    }, []);

    /** After Syncfusion upload succeeds, replace the blob src with the stable proxy URL from server */
    const handleImageUploadSuccess = useCallback((args) => {
        try {
            const response = JSON.parse(args.e?.currentTarget?.response || '{}');
            if (response.url) {
                // Update file name so Syncfusion constructs the correct URL (path + file.name)
                if (args.file) {
                    const key = response.url.replace(/^\/api\/uploads\/rte-content\//, '');
                    args.file.name = key;
                }
                // Also directly set the element src as a safety measure
                if (args.element) {
                    args.element.src = response.url;
                }
                // Sync RTE content back to form state after DOM update
                setTimeout(() => {
                    try {
                        const html = rteInfoRef.current?.inputElement?.innerHTML;
                        if (html != null) setField('information', html);
                    } catch (e) { /* ignore */ }
                }, 500);
            }
        } catch (err) {
            console.error('Failed to set image URL from server response:', err);
        }
    }, []);

    /** Handle file upload for video/audio with progress tracking */
    const handleFileUploading = useCallback(async (args) => {
        console.log('🎥 File uploading triggered:', args);
        
        const file = args.fileData?.rawFile;
        console.log('📁 File detected:', file, 'Type:', file?.type);
        
        if (!file || !rteInfoRef?.current) {
            console.error('❌ No file or RTE ref');
            args.cancel = true;
            return;
        }
        
        const isVideo = isVideoFile(file);
        const isAudio = isAudioFile(file);
        
        if (!isVideo && !isAudio) {
            console.error('❌ File is neither video nor audio:', file.type);
            showToast('Please select a video or audio file', 'error');
            args.cancel = true;
            return;
        }
        
        // Cancel Syncfusion's default upload
        args.cancel = true;
        
        // Close the Syncfusion dialog immediately (helper has internal retries)
        closeRteMediaDialog(rteInfoRef.current);
        
        // Show our custom progress modal
        setUploadingFile(file.name);
        setUploadProgress(0);
        setIsUploading(true);
        
        try {
            let downloadUrl;
            
            const onProgress = (percent) => {
                setUploadProgress(percent);
            };
            
            if (isVideo) {
                console.log('⬆️ Uploading video to S3...');
                const result = await uploadVideoToS3(file, onProgress);
                downloadUrl = result.downloadUrl;
                console.log('✅ Video uploaded, URL:', downloadUrl);
                
                // Insert video with proper attributes
                const videoHTML = generateVideoHTML(downloadUrl, file.type);
                rteInfoRef.current.executeCommand('insertHTML', videoHTML);
                const isMov = (file.name || '').toLowerCase().endsWith('.mov');
                showToast(isMov ? 'Video uploaded. If it doesn\'t play, try converting to MP4.' : 'Video uploaded successfully', 'success');
            } else if (isAudio) {
                console.log('⬆️ Uploading audio to S3...');
                const result = await uploadAudioToS3(file, onProgress);
                downloadUrl = result.downloadUrl;
                console.log('✅ Audio uploaded, URL:', downloadUrl);
                
                // Insert audio with proper attributes
                const audioHTML = generateAudioHTML(downloadUrl, file.type);
                rteInfoRef.current.executeCommand('insertHTML', audioHTML);
                showToast('Audio uploaded successfully', 'success');
            }
            
        } catch (err) {
            console.error('❌ Media upload failed:', err);
            showToast(isVideo ? 'Failed to upload video' : 'Failed to upload audio', 'error');
        } finally {
            // Hide progress modal after a brief delay
            setTimeout(() => {
                setIsUploading(false);
                setUploadingFile(null);
                setUploadProgress(0);
            }, 500);
        }
    }, [showToast]);

    const [events, setEvents] = useState([]);
    const [eventsTotalCount, setEventsTotalCount] = useState(0);
    const [loadingList, setLoadingList] = useState(false);
    const [termsOptions, setTermsOptions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    // Keep the status filter and search query in the URL (?status= / ?search=) so
    // they survive navigation and reloads without any browser storage.
    const [statusFilter, setStatusFilter] = useQueryParamState('status', '');
    const searchInputRef = useRef(null);
    const [activeSearchQuery, setActiveSearchQuery] = useQueryParamState('search', '');
    const [searchTriggerNonce, setSearchTriggerNonce] = useState(0);
    const loadRequestGenRef = useRef(0); // Generation counter to discard stale API responses

    const handleSearch = useCallback(() => {
        const query = (searchInputRef.current?.value || '').trim();
        setActiveSearchQuery(query);
        setSearchTriggerNonce((prev) => prev + 1);
        setCurrentPage(1); // Reset to first page when searching
    }, [setActiveSearchQuery]);

    const handleClearSearch = useCallback(() => {
        if (searchInputRef.current) {
            searchInputRef.current.value = '';
        }
        setActiveSearchQuery('');
        // Bump the nonce so loadEvents is always recreated and re-fetches, even
        // when the active query is already empty (otherwise clearing is a no-op
        // and the grid keeps showing stale results).
        setSearchTriggerNonce((prev) => prev + 1);
        setCurrentPage(1); // Reset to first page when clearing
    }, [setActiveSearchQuery]);


    const statusOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'draft', label: 'Draft' },
        { value: 'published', label: 'Published' },
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'archived', label: 'Archived' },
    ];

    // Keep the (uncontrolled) search input in sync with the ?search= query param,
    // including on mount and when navigating back/forward.
    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.value = activeSearchQuery || '';
        }
    }, [activeSearchQuery]);

    const loadEvents = useCallback(async () => {
        // Track this specific request so stale responses can be discarded
        const gen = ++loadRequestGenRef.current;

        try {
            setLoadingList(true);
            // Fetch a very large number (10000) to ensure ALL records are loaded for client-side pagination and filtering
            // This allows all events to be displayed whether searching or not
            const limit = 10000;
            const params = { page: 1, limit };
            // Add status filter if selected
            if (statusFilter && statusFilter.trim()) {
                params.status = statusFilter.trim();
            }
            const res = await listEvents(params);
            const getTotalCount = (response) => (
                Number(response?.pagination?.totalCount) ||
                Number(response?.totalCount) ||
                Number(response?.total) ||
                Number(response?.events?.length) ||
                0
            );
            let totalCount = getTotalCount(res);
            // Keep org event usage independent of status filtering for limit gating
            if (user?.role === 'Admin') {
                const fullRes = await listEvents({ page: 1, limit: 1 });
                totalCount = getTotalCount(fullRes);
            }
            setEventsTotalCount(totalCount);
            let items = res?.events || [];
            
            // Client-side filtering if search query exists
            if (activeSearchQuery && activeSearchQuery.trim()) {
                const searchLower = activeSearchQuery.trim().toLowerCase();
                items = items.filter(e => {
                    const name = (e.name || '').toLowerCase();
                    const description = (e.description || '').toLowerCase();
                    const slug = (e.slug || '').toLowerCase();
                    const sendyId = (e.sendyId || '').toLowerCase();
                    const status = (e.status || '').toLowerCase();
                    // Also search in formatted date strings
                    let startDate = '';
                    let endDate = '';
                    let createdDate = '';
                    try {
                        if (e.start) {
                            const start = new Date(e.start);
                            if (!isNaN(start.getTime())) {
                                startDate = start.toLocaleString().toLowerCase();
                            }
                        }
                        if (e.end) {
                            const end = new Date(e.end);
                            if (!isNaN(end.getTime())) {
                                endDate = end.toLocaleString().toLowerCase();
                            }
                        }
                        if (e.createdAt) {
                            const created = new Date(e.createdAt);
                            if (!isNaN(created.getTime())) {
                                createdDate = created.toDateString().toLowerCase();
                            }
                        }
                    } catch (err) {
                        // Ignore date parsing errors
                    }
                    const maxRecruiters = String(e.limits?.maxRecruitersPerEvent ?? 0);
                    const maxBooths = String(e.limits?.maxBooths ?? 0);
                    
                    return name.includes(searchLower) || 
                           description.includes(searchLower) || 
                           slug.includes(searchLower) ||
                           sendyId.includes(searchLower) ||
                           status.includes(searchLower) ||
                           startDate.includes(searchLower) ||
                           endDate.includes(searchLower) ||
                           createdDate.includes(searchLower) ||
                           maxRecruiters.includes(searchLower) ||
                           maxBooths.includes(searchLower);
                });
            }
            
            // Discard results if a newer request has already started
            if (gen !== loadRequestGenRef.current) return;

            setEvents(items.map((e) => ({
                id: e._id,
                name: e.name,
                link: e.link,
                slug: e.slug,
                sendyId: e.sendyId,
                description: e.description || '',
                logoUrl: e.logoUrl || '',
                logoAltText: e.logoAltText || '',
                startTime: e.start,
                endTime: e.end,
                date: new Date(e.start).toDateString(),
                createdAt: new Date(e.createdAt).toDateString(),
                maxRecruitersPerEvent: e.limits?.maxRecruitersPerEvent ?? 0,
                maxBooths: e.limits?.maxBooths ?? 0,
                registrationCount: e.stats?.totalRegistrations ?? 0,
                status: e.status || 'draft',
                isDemo: e.isDemo || false,
                addFooter: Boolean((e.addFooter !== undefined ? e.addFooter : e.theme?.addFooter)),
                termsIds: e.termsIds || [],
            })));
        } catch (err) {
            if (gen === loadRequestGenRef.current) {
                console.error('Failed to load events', err);
                setEvents([]);
                setEventsTotalCount(0);
            }
        } finally {
            if (gen === loadRequestGenRef.current) {
                setLoadingList(false);
            }
        }
    }, [statusFilter, activeSearchQuery, user, searchTriggerNonce]);

    const loadTerms = useCallback(async () => {
        try {
            const res = await termsConditionsAPI.getAll({ page: 1, limit: 100 });
            const list = res?.terms || [];
            // Only active terms can be attached to an event
            const activeList = list.filter(t => t.isActive);
            setTermsOptions(activeList.map(t => ({ value: t._id, label: `${t.title} (v${t.version}) • Active` })));
        } catch (e) {
            console.error('Failed to load terms', e);
        }
    }, []);

    // Get selected events from grid
    const getSelectedEventsFromGrid = useCallback(() => {
        if (!gridRef.current) return [];
        
        try {
            if (typeof gridRef.current.getSelectedRecords === 'function') {
                const selectedRows = gridRef.current.getSelectedRecords();
                return selectedRows.map(row => row.id || row._id).filter(Boolean);
            }
            
            if (typeof gridRef.current.getSelectedRowsData === 'function') {
                const selectedRows = gridRef.current.getSelectedRowsData();
                return selectedRows.map(row => row.id || row._id).filter(Boolean);
            }
            
            return [];
        } catch (error) {
            console.error('Error getting selected rows:', error);
            return [];
        }
    }, []);

    // Load events on mount and when filters change.
    // Debounce so rapid Search/Clear collapse into a single request. Direct
    // loadEvents() calls (delete, save, etc.) remain immediate.
    useEffect(() => {
        if (loading) return;
        const t = setTimeout(() => { loadEvents(); }, 250);
        return () => clearTimeout(t);
    }, [loading, loadEvents]);

    // Memoize the current page slice so the grid dataSource keeps a stable reference
    // across unrelated re-renders (selection, hover) and only recomputes on data/page change.
    const paginatedEvents = useMemo(
        () => events.slice((currentPage - 1) * pageSize, currentPage * pageSize),
        [events, currentPage, pageSize]
    );

    // Syncfusion Grid does not reliably pick up dataSource prop changes. Calling
    // refresh() alone only re-renders the grid's *existing* internal dataSource, so
    // searched/filtered results only appeared after a full page reload remounted the
    // grid. Assigning the new array to the grid instance forces EJ2 to rebind to the
    // fresh data (which also re-renders templated cells).
    useEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;
        grid.dataSource = paginatedEvents;
    }, [paginatedEvents]);

    // Track selection changes from grid
    useEffect(() => {
        if (!gridRef.current) return;
        
        const updateSelection = () => {
            const currentSelection = getSelectedEventsFromGrid();
            setSelectedEvents(currentSelection);
        };

        // Listen for selection events
        const grid = gridRef.current;
        if (grid.element) {
            const handleSelectionChange = () => {
                setTimeout(updateSelection, 100);
            };
            
            grid.element.addEventListener('click', handleSelectionChange);
            
            return () => {
                if (grid.element) {
                    grid.element.removeEventListener('click', handleSelectionChange);
                }
            };
        }
    }, [events, getSelectedEventsFromGrid]);

    // Load terms only when user enters create or edit mode (not needed for list view)
    useEffect(() => {
        if ((mode === 'create' || mode === 'edit') && termsOptions.length === 0) {
            loadTerms();
        }
    }, [mode, loadTerms, termsOptions.length]);

    // Frozen columns rely on native Syncfusion movable/frozen pane scrolling.
    useEffect(() => undefined, [events]);

    // Center delete dialog when it opens
    useEffect(() => {
        if (confirmOpen && deleteDialogRef.current) {
            const dialogElement = deleteDialogRef.current.element || deleteDialogRef.current;
            if (dialogElement) {
                // Wait for dialog to render
                setTimeout(() => {
                    const dialog = document.querySelector('.em-delete-dialog.e-dialog');
                    if (dialog) {
                        dialog.style.position = 'fixed';
                        dialog.style.top = '50%';
                        dialog.style.left = '50%';
                        dialog.style.transform = 'translate(-50%, -50%)';
                        dialog.style.margin = '0';
                    }
                }, 10);
            }
        }
    }, [confirmOpen]);

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast('Registration link copied');
        } catch (e) {
            console.error('Copy failed', e);
            window.prompt('Copy to clipboard: Ctrl+C, Enter', text);
            showToast('Copy failed. Link shown.');
        }
    };

    const registrationUrlFor = (row) => `${window.location.origin}/register?redirect=${encodeURIComponent(`/event/${row.slug}/register`)}`;

    const eventPageUrlFor = (row) => row.link || `${window.location.origin}/event/${row.slug}`;

    const previewUrlFor = (row) => `/events/registered/${row.slug}?preview=1`;

    // Grid template functions for custom column renders - using Syncfusion ButtonComponent
    // Format date/time to show the time in user's local timezone
    const formatEventDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        // Format date in local timezone
        const dateStr = date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
        // Format time in local timezone
        const timeStr = date.toLocaleString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        return `${dateStr}, ${timeStr}`;
    };

    const eventPageTemplate = (props) => (
        <ButtonComponent 
            cssClass="e-outline e-primary e-small" 
            onClick={() => window.open(eventPageUrlFor(props), '_blank')}
        >
            Event Page
        </ButtonComponent>
    );

    const previewTemplate = (props) => (
        <ButtonComponent 
            cssClass="e-outline e-primary e-small" 
            onClick={() => navigate(previewUrlFor(props))}
            title="Preview the job seeker registration page"
        >
            Preview
        </ButtonComponent>
    );

    const actionsTemplate = (props) => (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <ButtonComponent 
                cssClass="e-primary e-small" 
                onClick={() => copyText(registrationUrlFor(props))}
            >
                Registration Link
            </ButtonComponent>
            <ButtonComponent 
                cssClass="e-outline e-primary e-small" 
                onClick={() => startEdit(props)}
            >
                Edit
            </ButtonComponent>
            <ButtonComponent 
                cssClass="e-outline e-danger e-small" 
                onClick={() => handleDelete(props)}
            >
                Delete
            </ButtonComponent>
        </div>
    );

    const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    // Reorder selected Terms & Conditions. The order of `termsIds` determines the
    // order they are presented to job seekers during registration.
    const moveTerm = (index, direction) => {
        setForm(prev => {
            const ids = [...(prev.termsIds || [])];
            const target = index + direction;
            if (target < 0 || target >= ids.length) return prev;
            [ids[index], ids[target]] = [ids[target], ids[index]];
            return { ...prev, termsIds: ids };
        });
    };

    const resetForm = () => {
        // Clear RichTextEditor content first if ref exists
        if (rteInfoRef.current) {
            try {
                rteInfoRef.current.value = '';
                rteInfoRef.current.refresh();
            } catch (e) {
                // Ignore errors if RTE is not initialized
                console.debug('RTE reset:', e);
            }
        }
        setForm({
            sendyId: '',
            name: '',
            link: '',
            logoUrl: '',
            logoAltText: '',
            maxBooths: 0,
            maxRecruitersPerEvent: 0,
            startTime: '',
            endTime: '',
            information: '',
            termsId: '',
            termsIds: [],
            status: 'draft',
            isDemo: false,
        });
        setManualSendyId('');
        setEditingId(null);
    };

    const handleNew = () => {
        const orgEventLimit = Number(user?.organizationId?.limits?.maxEvents || 0);
        const orgEventLimitReached = orgEventLimit > 0 && eventsTotalCount >= orgEventLimit;
        if (orgEventLimitReached) {
            showToast(`Event limit reached (${orgEventLimit}). You cannot create more events.`, 'Error');
            return;
        }
        resetForm();
        setMode('create');
    };

    const onPickLogo = async (file) => {
        if (!file) return;
        const validation = validateLogoFile(file);
        if (!validation.ok) {
            alert(validation.message);
            return;
        }
        try {
            setSaving(true);
            const { downloadUrl } = await uploadImageToS3(file, { variant: 'public' });
            setField('logoUrl', downloadUrl);
        } catch (e) {
            console.error('Logo upload failed', e);
            alert(getLogoUploadErrorMessage(e, 'Failed to upload logo. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (row) => {
        setEditingId(row.id);
        
        // Helper function to convert UTC date to local datetime-local format
        const toLocalDateTimeString = (dateString) => {
            if (!dateString) return '';
            const date = new Date(dateString);
            // Get local date/time components
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };
        
        setForm(prev => ({
            ...prev,
            sendyId: row.sendyId || '',
            name: row.name || '',
            link: row.link || '',
            logoUrl: row.logoUrl || '',
            logoAltText: row.logoAltText || '',
            maxBooths: row.maxBooths || 0,
            maxRecruitersPerEvent: row.maxRecruitersPerEvent || 0,
            startTime: toLocalDateTimeString(row.startTime),
            endTime: toLocalDateTimeString(row.endTime),
            information: row.description || '',
            status: row.status || 'draft',
            isDemo: row.isDemo || false,
            termsIds: row.termsIds || [],
        }));
        setMode('edit');
    };

    const handleDelete = (row) => {
        setRowPendingDelete(row);
        setConfirmOpen(true);
    };

    const confirmDelete = async () => {
        if (!rowPendingDelete) return;
        try {
            setSaving(true);
            await deleteEvent(rowPendingDelete.id);
            await loadEvents();
        } finally {
            setSaving(false);
            setConfirmOpen(false);
            setRowPendingDelete(null);
        }
    };

    const cancelDelete = () => {
        setConfirmOpen(false);
        setRowPendingDelete(null);
    };

    const handleBulkDelete = () => {
        const currentSelection = getSelectedEventsFromGrid();
        if (currentSelection.length === 0) {
            showToast('Please select events to delete', 'Warning');
            return;
        }
        setSelectedEvents(currentSelection);
        setConfirmBulkDeleteOpen(true);
    };

    const confirmBulkDelete = async () => {
        try {
            setIsDeleting(true);
            const response = await bulkDeleteEvents(selectedEvents);
            showToast(response.message || 'Events deleted successfully', 'Success');
            setSelectedEvents([]);
            await loadEvents();
        } catch (error) {
            console.error('Error deleting events:', error);
            showToast(error.response?.data?.message || 'Failed to delete events', 'Error');
        } finally {
            setIsDeleting(false);
            setConfirmBulkDeleteOpen(false);
        }
    };

    const cancelBulkDelete = () => {
        setConfirmBulkDeleteOpen(false);
        setSelectedEvents([]);
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        const orgEventLimit = Number(user?.organizationId?.limits?.maxEvents || 0);
        const orgEventLimitReached = orgEventLimit > 0 && eventsTotalCount >= orgEventLimit;
        if (mode !== 'edit' && orgEventLimitReached) {
            showToast(`Event limit reached (${orgEventLimit}). You cannot create more events.`, 'Error');
            return;
        }
        setSaving(true);
        try {
            // Convert datetime-local format to ISO string
            // datetime-local gives us "YYYY-MM-DDTHH:MM" in user's local time
            // We need to convert it to ISO string for the server
            const startISO = form.startTime ? new Date(form.startTime).toISOString() : undefined;
            const endISO = form.endTime ? new Date(form.endTime).toISOString() : undefined;

            // Read the latest HTML directly from RTE content area.
            // Image upload success handlers update the DOM img src attribute directly,
            // which may not trigger a React state update via the RTE change event.
            let latestInfo = form.information;
            try {
                if (rteInfoRef.current?.inputElement) latestInfo = rteInfoRef.current.inputElement.innerHTML;
                else if (rteInfoRef.current?.value != null) latestInfo = rteInfoRef.current.value;
            } catch (e) { /* fall through */ }
            
            const payload = {
                name: form.name,
                description: latestInfo,
                start: startISO,
                end: endISO,
                logoUrl: form.logoUrl || undefined,
                logoAltText: form.logoAltText || undefined,
                link: form.link?.trim() || undefined,
                sendyId: form.sendyId ? form.sendyId : null,
                termsId: form.termsId || undefined,
                termsIds: form.termsIds || [],
                status: form.status || undefined,
                limits: {
                    maxBooths: form.maxBooths || 0,
                    maxRecruitersPerEvent: form.maxRecruitersPerEvent || 0,
                },
                theme: {
                    headerColor: '#ffffff',
                    headerTextColor: '#000000',
                    bodyColor: '#ff9800',
                    bodyTextColor: '#000000',
                    sidebarColor: '#ffffff',
                    sidebarTextColor: '#000000',
                    btnPrimaryColor: '#000000',
                    btnPrimaryTextColor: '#ffffff',
                    btnSecondaryColor: '#000000',
                    btnSecondaryTextColor: '#ffffff',
                    entranceFormColor: '#ff9800',
                    entranceFormTextColor: '#000000',
                    chatHeaderColor: '#eeeeee',
                    chatSidebarColor: '#000000',
                    addFooter: false,
                },
                isDemo: !!form.isDemo,
            };

            if (mode === 'edit' && editingId) {
                await updateEvent(editingId, payload);
            } else {
                await createEvent(payload);
            }
            await loadEvents();
            resetForm();
            setMode('list');
        } catch (err) {
            console.error('Failed to save event', err);
            const data = err?.response?.data;
            const details = Array.isArray(data?.details)
                ? data.details.map((d) => `${d.path || d.param || 'field'}: ${d.msg}`).join('; ')
                : '';
            const message = [data?.message || 'Failed to save event', details].filter(Boolean).join(' — ');
            showToast(message, 'Error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="dashboard">
            <a href="#main-content" className="skip-link">Skip to main content</a>
            {/* Video Upload Progress Modal */}
            <VideoUploadProgress 
                progress={uploadProgress}
                fileName={uploadingFile}
                isUploading={isUploading}
            />
            
            <AdminHeader />

            <div className="dashboard-layout">
                <AdminSidebar active="events" />

                <main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content">
                    <div className="dashboard-content">
                        <PageInstructionBanner screen={mode === 'list' ? 'event-management' : 'event-create-edit-page'} />
                        <div className="bm-header">
                            <h1>Event Management</h1>
                            <div className="bm-header-actions">
                                {mode === 'list' ? (
                                    <>
                                        {selectedEvents.length > 0 && (
                                            <>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '12px' }}>
                                                    <input
                                                        type="checkbox"
                                                        id="select-all-events"
                                                        checked={selectedEvents.length > 0 && selectedEvents.length === paginatedEvents.length}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                // Select all rows on current page
                                                                if (gridRef.current) {
                                                                    const pageData = paginatedEvents;
                                                                    gridRef.current.selectRows(Array.from({ length: pageData.length }, (_, i) => i));
                                                                    // Manually update state to ensure checkbox reflects selection immediately
                                                                    setTimeout(() => {
                                                                        const currentSelection = getSelectedEventsFromGrid();
                                                                        setSelectedEvents(currentSelection);
                                                                    }, 100);
                                                                }
                                                            } else {
                                                                // Deselect all rows
                                                                if (gridRef.current) {
                                                                    gridRef.current.clearSelection();
                                                                    setSelectedEvents([]);
                                                                }
                                                            }
                                                        }}
                                                        style={{ 
                                                            width: '18px', 
                                                            height: '18px', 
                                                            cursor: 'pointer',
                                                            accentColor: '#000000'
                                                        }}
                                                    />
                                                    <label htmlFor="select-all-events" style={{ cursor: 'pointer', userSelect: 'none', fontSize: '14px', fontWeight: '500' }}>
                                                        Select All
                                                    </label>
                                                </div>
                                                <ButtonComponent 
                                                    cssClass="e-danger"
                                                    onClick={handleBulkDelete}
                                                    disabled={isDeleting}
                                                    aria-label={`Delete ${selectedEvents.length} selected events`}
                                                >
                                                    {isDeleting ? 'Deleting...' : `Delete Selected (${selectedEvents.length})`}
                                                </ButtonComponent>
                                            </>
                                        )}
                                        <ButtonComponent
                                            cssClass="e-primary"
                                            onClick={handleNew}
                                            disabled={Number(user?.organizationId?.limits?.maxEvents || 0) > 0 && eventsTotalCount >= Number(user?.organizationId?.limits?.maxEvents || 0)}
                                            title={(Number(user?.organizationId?.limits?.maxEvents || 0) > 0 && eventsTotalCount >= Number(user?.organizationId?.limits?.maxEvents || 0))
                                                ? `Event limit reached (${Number(user?.organizationId?.limits?.maxEvents || 0)})`
                                                : 'Create Event'}
                                        >
                                            Create Event
                                        </ButtonComponent>
                                        {(Number(user?.organizationId?.limits?.maxEvents || 0) > 0 && eventsTotalCount >= Number(user?.organizationId?.limits?.maxEvents || 0)) && (
                                            <span style={{ fontSize: '12px', color: '#b91c1c', marginLeft: '8px' }}>
                                                Event limit reached ({eventsTotalCount}/{Number(user?.organizationId?.limits?.maxEvents || 0)})
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <ButtonComponent cssClass="e-outline e-primary" onClick={() => { resetForm(); setMode('list'); }}>
                                        Back to List
                                    </ButtonComponent>
                                )}
                            </div>
                        </div>

                        {mode === 'list' ? (
                            <div className="bm-grid-wrap" style={{ position: 'relative' }}>
                                <div className="em-filters-row" style={{ marginBottom: 12, paddingLeft: '20px', paddingRight: '20px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    {/* Status Filter - Left */}
                                    <div style={{ width: '200px', flexShrink: 0 }}>
                                        <DropDownListComponent
                                            id="status-filter-dropdown"
                                            dataSource={statusOptions.map(s => ({ value: s.value, text: s.label }))}
                                            fields={{ value: 'value', text: 'text' }}
                                            value={statusFilter}
                                            change={(e) => {
                                                setStatusFilter(e.value || '');
                                                setCurrentPage(1); // Reset to first page when filter changes
                                                // loadEvents will be called automatically via useEffect when statusFilter changes
                                            }}
                                            placeholder="Select Status"
                                            cssClass="status-filter-dropdown"
                                            popupHeight="300px"
                                            width="100%"
                                        />
                                    </div>
                                    {/* Search Section - Right */}
                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginLeft: 'auto' }}>
                                        <div style={{ marginBottom: 0 }}>
                                            <input
                                                ref={searchInputRef}
                                                id="event-search-input"
                                                type="text"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleSearch();
                                                    }
                                                }}
                                                placeholder="Search by name, email, or any field..."
                                                style={{ width: '300px', marginBottom: 0, padding: '10px 12px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '14px' }}
                                                className="em-search-input-native"
                                            />
                                        </div>
                                        <ButtonComponent
                                            cssClass="e-primary e-small"
                                            onClick={handleSearch}
                                            disabled={loadingList}
                                            aria-label="Search events"
                                            style={{ minWidth: '80px', height: '44px' }}
                                        >
                                            Search
                                        </ButtonComponent>
                                        {activeSearchQuery && (
                                            <ButtonComponent
                                                cssClass="e-outline e-primary e-small"
                                                onClick={handleClearSearch}
                                                disabled={loadingList}
                                                aria-label="Clear search"
                                                style={{ minWidth: '70px', height: '44px' }}
                                            >
                                                Clear
                                            </ButtonComponent>
                                        )}
                                    </div>
                                </div>
                                {loadingList && (
                                    <div className="em-grid-loading-overlay">
                                        <div className="em-loading-container">
                                            <div className="em-loading-spinner" aria-label="Loading events" role="status" aria-live="polite"></div>
                                            <div className="em-loading-text">Loading events...</div>
                                        </div>
                                    </div>
                                )}
                                <GridComponent
                                    ref={gridRef}
                                    dataSource={paginatedEvents}
                                    allowPaging={false}
                                    allowSorting={true}
                                    allowFiltering={true}
                                    filterSettings={SYNC_GRID_FILTER_SETTINGS}
                                    showColumnMenu={true}
                                    showColumnChooser={true}
                                    allowResizing={true}
                                    allowReordering={true}
                                    toolbar={['ColumnChooser']}
                                    selectionSettings={{ 
                                        type: 'Multiple', 
                                        checkboxOnly: true,
                                        persistSelection: false,
                                        enableSimpleMultiRowSelection: false,
                                        checkboxMode: 'Default'
                                    }}
                                    enableHover={true}
                                    allowRowDragAndDrop={false}
                                    enableHeaderFocus={false}
                                >
                                    <ColumnsDirective>
                                        <ColumnDirective {...SYNC_GRID_CHECKBOX_COLUMN_PROPS} />
                                        <ColumnDirective 
                                            field='name' 
                                            headerText='Event Name' 
                                            width='200' 
                                            freeze='Left'
                                            type='string'
                                            allowFiltering={true}
                                            template={(props) => (
                                                <div style={{ 
                                                    wordWrap: 'break-word', 
                                                    wordBreak: 'break-word', 
                                                    whiteSpace: 'normal',
                                                    lineHeight: '1.5',
                                                    padding: '4px 0'
                                                }}>
                                                    {props.name || '-'}
                                                </div>
                                            )}
                                        />
                                        <ColumnDirective field='id' headerText='' width='0' isPrimaryKey={true} visible={false} showInColumnChooser={false} />
                                        <ColumnDirective 
                                            field='startTime' 
                                            headerText='Event Start Time' 
                                            width='200' 
                                            type='string'
                                            allowFiltering={true}
                                            template={(props) => (
                                                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0' }}>
                                                    {formatEventDateTime(props.startTime)}
                                                </div>
                                            )}
                                        />
                                        <ColumnDirective 
                                            field='endTime' 
                                            headerText='Event End Time' 
                                            width='200' 
                                            type='string'
                                            allowFiltering={true}
                                            template={(props) => (
                                                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0' }}>
                                                    {formatEventDateTime(props.endTime)}
                                                </div>
                                            )}
                                        />
                                        <ColumnDirective 
                                            field='date' 
                                            headerText='Event Date' 
                                            width='180' 
                                            allowFiltering={true}
                                            template={(props) => (
                                                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0' }}>
                                                    {props.date || '-'}
                                                </div>
                                            )}
                                        />
                                        <ColumnDirective 
                                            field='createdAt' 
                                            headerText='Created Time' 
                                            width='150' 
                                            allowFiltering={true}
                                            template={(props) => (
                                                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0' }}>
                                                    {props.createdAt || '-'}
                                                </div>
                                            )}
                                        />
                                        <ColumnDirective 
                                            field='status' 
                                            headerText='Status' 
                                            width='120' 
                                            allowFiltering={true}
                                            template={(props) => (
                                                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0' }}>
                                                    {props.status || '-'}
                                                </div>
                                            )}
                                        />
                                        <ColumnDirective 
                                            field='maxRecruitersPerEvent' 
                                            headerText='Max Recruiters' 
                                            width='130' 
                                            textAlign='Center'
                                            allowFiltering={true}
                                            template={(props) => (
                                                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center' }}>
                                                    {props.maxRecruitersPerEvent ?? 0}
                                                </div>
                                            )}
                                        />
                                        <ColumnDirective 
                                            field='maxBooths' 
                                            headerText='Max Booths' 
                                            width='110' 
                                            textAlign='Center'
                                            allowFiltering={true}
                                            template={(props) => (
                                                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center' }}>
                                                    {props.maxBooths ?? 0}
                                                </div>
                                            )}
                                        />
                                        <ColumnDirective 
                                            field='registrationCount' 
                                            headerText='Registered Jobseekers' 
                                            width='180' 
                                            textAlign='Center'
                                            allowFiltering={true}
                                            allowSorting={true}
                                            template={(props) => (
                                                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0', textAlign: 'center' }}>
                                                    {props.registrationCount ?? 0}
                                                </div>
                                            )}
                                        />
                                        <ColumnDirective 
                                            field='sendyId' 
                                            headerText='Sendy Event Id' 
                                            width='150' 
                                            allowFiltering={true}
                                            template={(props) => (
                                                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', padding: '8px 0' }}>
                                                    {props.sendyId || '-'}
                                                </div>
                                            )}
                                        />
                                        <ColumnDirective 
                                            headerText='Event Page' 
                                            width='130' 
                                            allowSorting={false} 
                                            allowFiltering={false}
                                            template={eventPageTemplate}
                                        />
                                        <ColumnDirective 
                                            headerText='Preview' 
                                            width='120' 
                                            allowSorting={false} 
                                            allowFiltering={false}
                                            template={previewTemplate}
                                        />
                                        <ColumnDirective 
                                            headerText='Action' 
                                            width='360' 
                                            allowSorting={false} 
                                            allowFiltering={false}
                                            template={actionsTemplate}
                                        />
                                    </ColumnsDirective>
                                    <GridInject services={[Sort, Filter, GridToolbar, Selection, Resize, Reorder, ColumnChooser, ColumnMenu, Freeze]} />
                                </GridComponent>

                                {/* Custom Pagination Footer */}
                                {events.length > 0 && (
                                    <div className="custom-pagination" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '16px',
                                        backgroundColor: '#f9fafb',
                                        borderTop: '1px solid #e5e7eb',
                                        marginTop: '0'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <span style={{ fontSize: '14px', color: '#374151' }}>
                                                Rows per page:
                                            </span>
                                            <select
                                                value={pageSize}
                                                onChange={(e) => {
                                                    const newSize = parseInt(e.target.value);
                                                    setPageSize(newSize);
                                                    setCurrentPage(1);
                                                }}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #d1d5db',
                                                    fontSize: '14px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                                <option value={200}>200</option>
                                            </select>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '14px', color: '#374151' }}>
                                                Page {currentPage} of {Math.ceil(events.length / pageSize) || 1} ({events.length} total)
                                            </span>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <button
                                                onClick={() => {
                                                    if (currentPage > 1) {
                                                        setCurrentPage(1);
                                                    }
                                                }}
                                                disabled={currentPage <= 1 || loadingList}
                                                style={{
                                                    padding: '8px 12px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #d1d5db',
                                                    backgroundColor: currentPage <= 1 ? '#f3f4f6' : '#fff',
                                                    cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                                                    fontSize: '14px',
                                                    color: currentPage <= 1 ? '#9ca3af' : '#374151'
                                                }}
                                                title="First Page"
                                            >
                                                ⟨⟨
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (currentPage > 1) {
                                                        setCurrentPage(currentPage - 1);
                                                    }
                                                }}
                                                disabled={currentPage <= 1 || loadingList}
                                                style={{
                                                    padding: '8px 12px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #d1d5db',
                                                    backgroundColor: currentPage <= 1 ? '#f3f4f6' : '#fff',
                                                    cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                                                    fontSize: '14px',
                                                    color: currentPage <= 1 ? '#9ca3af' : '#374151'
                                                }}
                                                title="Previous Page"
                                            >
                                                ⟨ Prev
                                            </button>
                                            
                                            <input
                                                type="number"
                                                min="1"
                                                max={Math.ceil(events.length / pageSize) || 1}
                                                value={currentPage}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value);
                                                    const maxPage = Math.ceil(events.length / pageSize) || 1;
                                                    if (val >= 1 && val <= maxPage) {
                                                        setCurrentPage(val);
                                                    }
                                                }}
                                                style={{
                                                    width: '60px',
                                                    padding: '6px 8px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #d1d5db',
                                                    fontSize: '14px',
                                                    textAlign: 'center'
                                                }}
                                            />
                                            
                                            <button
                                                onClick={() => {
                                                    const maxPage = Math.ceil(events.length / pageSize) || 1;
                                                    if (currentPage < maxPage) {
                                                        setCurrentPage(currentPage + 1);
                                                    }
                                                }}
                                                disabled={currentPage >= (Math.ceil(events.length / pageSize) || 1) || loadingList}
                                                style={{
                                                    padding: '8px 12px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #d1d5db',
                                                    backgroundColor: currentPage >= (Math.ceil(events.length / pageSize) || 1) ? '#f3f4f6' : '#fff',
                                                    cursor: currentPage >= (Math.ceil(events.length / pageSize) || 1) ? 'not-allowed' : 'pointer',
                                                    fontSize: '14px',
                                                    color: currentPage >= (Math.ceil(events.length / pageSize) || 1) ? '#9ca3af' : '#374151'
                                                }}
                                                title="Next Page"
                                            >
                                                Next ⟩
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const maxPage = Math.ceil(events.length / pageSize) || 1;
                                                    if (currentPage < maxPage) {
                                                        setCurrentPage(maxPage);
                                                    }
                                                }}
                                                disabled={currentPage >= (Math.ceil(events.length / pageSize) || 1) || loadingList}
                                                style={{
                                                    padding: '8px 12px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #d1d5db',
                                                    backgroundColor: currentPage >= (Math.ceil(events.length / pageSize) || 1) ? '#f3f4f6' : '#fff',
                                                    cursor: currentPage >= (Math.ceil(events.length / pageSize) || 1) ? 'not-allowed' : 'pointer',
                                                    fontSize: '14px',
                                                    color: currentPage >= (Math.ceil(events.length / pageSize) || 1) ? '#9ca3af' : '#374151'
                                                }}
                                                title="Last Page"
                                            >
                                                ⟩⟩
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <form className="account-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }} style={{ maxWidth: 720, paddingBottom: 350 }}>
                                <div
                                    className="sendy-connection-section"
                                    style={{
                                        marginBottom: '1.25rem',
                                        padding: '1rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        backgroundColor: '#fafafa'
                                    }}
                                >
                                    <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Sendy Connection</h3>
                                    {form.sendyId ? (
                                        <div>
                                            <p style={{ margin: '0 0 0.5rem' }}>
                                                <strong>Status:</strong> Connected
                                            </p>
                                            <p style={{ margin: '0 0 0.5rem' }}>
                                                <strong>List ID:</strong> <code>{form.sendyId}</code>
                                            </p>
                                            {connectedSendyListName && (
                                                <p style={{ margin: '0 0 0.75rem' }}>
                                                    <strong>List name:</strong> {connectedSendyListName}
                                                </p>
                                            )}
                                            <ButtonComponent
                                                cssClass="e-outline e-danger"
                                                type="button"
                                                onClick={() => {
                                                    setField('sendyId', '');
                                                    setManualSendyId('');
                                                }}
                                            >
                                                Disconnect
                                            </ButtonComponent>
                                        </div>
                                    ) : (
                                        <div>
                                            <p style={{ margin: '0 0 0.75rem' }}>
                                                <strong>Status:</strong> Not connected
                                            </p>
                                            {sendyListsLoading ? (
                                                <p style={{ margin: 0 }}>Loading Sendy lists...</p>
                                            ) : (
                                                <>
                                                    {sendyLists.length > 0 && (
                                                        <Select
                                                            label="Select Sendy list"
                                                            value={manualSendyId}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                setManualSendyId(value);
                                                                setField('sendyId', value);
                                                            }}
                                                            options={[
                                                                { value: '', label: 'Choose a list...' },
                                                                ...sendyLists.map((list) => ({
                                                                    value: list.id,
                                                                    label: list.name
                                                                }))
                                                            ]}
                                                        />
                                                    )}
                                                    <Input
                                                        label={sendyLists.length > 0 ? 'Or enter Sendy list ID manually' : 'Enter Sendy list ID'}
                                                        value={manualSendyId}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setManualSendyId(value);
                                                            setField('sendyId', value);
                                                        }}
                                                        placeholder="Paste list ID from Sendy"
                                                    />
                                                    {sendyLists.length === 0 && !sendyListsLoading && (
                                                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                                                            Create the list in Sendy first, then connect it here by pasting the list ID.
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <Input label="Event Name" value={form.name} onChange={(e) => setField('name', e.target.value)} required placeholder="" />
                                <Input label="Event Link" value={form.link} onChange={(e) => setField('link', e.target.value)} placeholder="https://..." />

                                <Select
                                    id="event-status"
                                    label="Status"
                                    value={form.status}
                                    onChange={(e) => setField('status', e.target.value)}
                                    aria-describedby="event-status-hint"
                                    options={[
                                        { value: 'draft', label: 'Draft' },
                                        { value: 'published', label: 'Published' },
                                        { value: 'active', label: 'Active' },
                                        { value: 'completed', label: 'Completed' },
                                        { value: 'cancelled', label: 'Cancelled' },
                                        { value: 'archived', label: 'Archived' },
                                    ]}
                                />
                                <p id="event-status-hint" className="form-hint">
                                    Job seekers only see Published and Active events, including in their
                                    current registrations. Archived also hides the event and its data from
                                    recruiters, booth admins, interpreters and support. Registrations are kept,
                                    so setting the status back restores them.
                                </p>

                                <div className="form-group">
                                    <label className="form-label">Event Logo</label>
                                    <div className="upload-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                        <ButtonComponent 
                                            cssClass="e-outline e-primary e-small"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                document.getElementById('logo-upload')?.click();
                                            }}
                                        >
                                            Choose file
                                        </ButtonComponent>
                                        <input 
                                            id="logo-upload"
                                            type="file" 
                                            accept={LOGO_ACCEPT} 
                                            onChange={(e) => onPickLogo(e.target.files?.[0])} 
                                            style={{ display: 'none' }} 
                                        />
                                        {form.logoUrl && <img src={form.logoUrl} alt="Event logo" style={{ height: 40, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', padding: 4 }} />}
                                    </div>
                                </div>

                                <Input 
                                    label="Event Logo Alt Text (for screen readers)" 
                                    type="text" 
                                    value={form.logoAltText} 
                                    onChange={(e) => setField('logoAltText', e.target.value)} 
                                    placeholder="e.g., Company Name Event Logo"
                                    maxLength="200"
                                />

                                <Input label="Maximum Number of Booths" type="number" min="0" value={form.maxBooths} onChange={(e) => setField('maxBooths', Number(e.target.value))} />
                                <Input label="Maximum Recruiters Per Event" type="number" min="0" value={form.maxRecruitersPerEvent} onChange={(e) => setField('maxRecruitersPerEvent', Number(e.target.value))} />

                                <div className="form-row">
                                    <DateTimePicker
                                        label="Event Start Time"
                                        value={form.startTime}
                                        onChange={(e) => setField('startTime', e.target.value)}
                                        placeholder="Select start"
                                        disabled={form.isDemo}
                                    />
                                    <DateTimePicker
                                        label="Event End Time"
                                        value={form.endTime}
                                        onChange={(e) => setField('endTime', e.target.value)}
                                        placeholder="Select end"
                                        disabled={form.isDemo}
                                    />
                                </div>

                                <Checkbox
                                    label="Demo Event (always available, no specific start/end date)"
                                    checked={form.isDemo}
                                    onChange={(e) => setField('isDemo', e.target.checked)}
                                />

                                <div className="form-group">
                                    <label className="form-label">Event Information</label>
                                    <RTE
                                        ref={rteInfoRef}
                                        value={form.information}
                                        change={(e) => setField('information', e?.value || '')}
                                        placeholder="Type event details..."
                                        toolbarSettings={rteToolbarSettings}
                                        quickToolbarSettings={RTE_QUICK_TOOLBAR_SETTINGS}
                                        insertImageSettings={getInsertImageSettings()}
                                        insertVideoSettings={getInsertVideoSettings()}
                                        insertAudioSettings={getInsertAudioSettings()}
                                        height={550}
                                        enableXhtml={true}
                                        fileUploading={handleFileUploading}
                                        imageUploading={handleImageUploading}
                                        imageUploadSuccess={handleImageUploadSuccess}
                                        keyDown={handleRteKeyDown}
                                    >
                                        <RTEInject services={[HtmlEditor, RTEToolbar, QuickToolbar, RteLink, RteImage, Table, Video, Audio, EmojiPicker, PasteCleanup, Count, RTEResize, FormatPainter]} />
                                    </RTE>
                                </div>

                                <MultiSelect
                                    label="Select Terms and Conditions (multiple)"
                                    value={form.termsIds}
                                    onChange={(e) => setField('termsIds', e.target.value)}
                                    options={termsOptions}
                                    placeholder="Choose one or more Terms & Conditions"
                                />

                                {form.termsIds.length > 1 && (
                                    <div className="form-group">
                                        <label className="form-label">Terms and Conditions Order</label>
                                        <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#6c757d' }}>
                                            Use the arrows to set the order job seekers see these during registration.
                                        </p>
                                        <ul className="terms-reorder-list" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {form.termsIds.map((id, index) => {
                                                const option = termsOptions.find(o => o.value === id);
                                                const label = option?.label || id;
                                                return (
                                                    <li
                                                        key={id}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: '12px',
                                                            padding: '10px 12px',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '8px',
                                                            background: '#fff'
                                                        }}
                                                    >
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                                                            <span style={{
                                                                flexShrink: 0,
                                                                width: '22px',
                                                                height: '22px',
                                                                borderRadius: '50%',
                                                                background: '#111111',
                                                                color: '#fff',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '12px',
                                                                fontWeight: 600
                                                            }}>
                                                                {index + 1}
                                                            </span>
                                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {label}
                                                            </span>
                                                        </span>
                                                        <span style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                                            <ButtonComponent
                                                                cssClass="e-outline e-primary e-small"
                                                                disabled={index === 0}
                                                                onClick={(e) => { e.preventDefault(); moveTerm(index, -1); }}
                                                                aria-label={`Move ${label} up`}
                                                                title="Move up"
                                                            >
                                                                ↑
                                                            </ButtonComponent>
                                                            <ButtonComponent
                                                                cssClass="e-outline e-primary e-small"
                                                                disabled={index === form.termsIds.length - 1}
                                                                onClick={(e) => { e.preventDefault(); moveTerm(index, 1); }}
                                                                aria-label={`Move ${label} down`}
                                                                title="Move down"
                                                            >
                                                                ↓
                                                            </ButtonComponent>
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}

                                <ButtonComponent 
                                    cssClass="e-primary" 
                                    disabled={saving}
                                    isPrimary={true}
                                    onClick={() => handleSubmit({ preventDefault: () => {} })}
                                >
                                    {saving ? 'Saving…' : (mode === 'edit' ? 'Update' : 'Create')}
                                </ButtonComponent>
                            </form>
                        )}
                    </div>
                </main>
            </div>
            <DialogComponent
                ref={deleteDialogRef}
                width="450px"
                isModal={true}
                showCloseIcon={true}
                visible={confirmOpen}
                header="Delete Event"
                closeOnEscape={true}
                close={cancelDelete}
                cssClass="em-delete-dialog"
                buttons={[
                    {
                        buttonModel: {
                            content: 'Cancel',
                            isPrimary: false,
                            cssClass: 'e-outline e-primary'
                        },
                        click: () => {
                            cancelDelete();
                        }
                    },
                    {
                        buttonModel: {
                            content: saving ? 'Deleting…' : 'Delete',
                            isPrimary: true,
                            cssClass: 'e-danger'
                        },
                        click: () => {
                            confirmDelete();
                        }
                    }
                ]}
            >
                <p style={{ margin: 0, lineHeight: '1.5' }}>
                    Are you sure you want to delete <strong>{rowPendingDelete?.name}</strong>? This action cannot be undone.
                </p>
            </DialogComponent>

            {/* Bulk Delete confirm modal - Syncfusion DialogComponent */}
            <DialogComponent
                width="450px"
                isModal={true}
                showCloseIcon={true}
                visible={confirmBulkDeleteOpen}
                header="Bulk Delete Events"
                closeOnEscape={true}
                close={cancelBulkDelete}
                cssClass="em-delete-dialog"
                buttons={[
                    {
                        buttonModel: {
                            content: 'Cancel',
                            isPrimary: false,
                            cssClass: 'e-outline e-primary'
                        },
                        click: () => {
                            cancelBulkDelete();
                        }
                    },
                    {
                        buttonModel: {
                            content: isDeleting ? 'Deleting...' : 'Delete',
                            isPrimary: true,
                            cssClass: 'e-danger'
                        },
                        click: () => {
                            confirmBulkDelete();
                        }
                    }
                ]}
            >
                <p style={{ margin: 0, lineHeight: '1.5' }}>
                    Are you sure you want to permanently delete <strong>{selectedEvents.length} event(s)</strong>? This action cannot be undone.
                </p>
            </DialogComponent>

            <ToastComponent 
                ref={(toast) => toastRef.current = toast}
                position={{ X: 'Right', Y: 'Bottom' }}
                showProgressBar={true}
                timeOut={3000}
                newestOnTop={true}
            />
        </div>
    );
}
